import React from 'react';
import { Box } from '@mui/material';
import { DataTable, Panel, Sparkline, type DataTableColumn } from '../../../../ui-kit';
import type { ReportPayload, ReportRow, ReportTable } from '../../../../types/reportDashboard';
import { formatReportValue } from '../reportFormat';
import { useReportCohort, type ReportQuery } from '../useReportCohort';
import type { ReportDownload } from '../useReportDownload';
import TermLabel from './TermLabel';

// The same measures as the cohort board's tables, so the two read alike.
const METRIC_WIDTH = 300;
const TREND_WIDTH = 136;
const PERIOD_MIN_WIDTH = 84;
const SPARKLINE_WIDTH = 104;
const SPARKLINE_HEIGHT = 20;
const SPARKLINE_MIN_POINTS = 3;

/**
 * Her terms down the side and the cohorts across, laid out like every board
 * table: Metric, Trend, the Total, then each cohort newest first. Each cohort
 * is the applications submitted in it, and where they stand now.
 *
 * The table has its own Cohort choice, and its download follows it. A term
 * that is not recorded keeps its row, empty, with its yellow info icon.
 */
export default function MonthTable({ block, payload, query, download, loading }: {
  /** The payload key, which is also what the download asks for. */
  block: 'dashboard_table';
  payload: ReportPayload;
  query: ReportQuery;
  download: ReportDownload;
  loading?: boolean;
}) {
  const cohort = useReportCohort(payload, query);
  const table: ReportTable = cohort.source[block];
  const periods = cohort.source.periods;
  const newestFirst = periods.map((p, i) => ({ ...p, i })).reverse();
  const drawLine = periods.length >= SPARKLINE_MIN_POINTS;

  const columns: DataTableColumn<ReportRow>[] = [
    {
      key: 'term',
      header: 'Metric',
      width: METRIC_WIDTH,
      sticky: true,
      render: (r) => <TermLabel term={r} />,
    },
    {
      key: 'trend',
      header: 'Trend',
      width: TREND_WIDTH,
      render: (r) => (r.missing || !drawLine ? null : (
        <Sparkline
          points={r.values.map((v, i) => ({ key: periods[i].key, label: periods[i].label, value: v }))}
          width={SPARKLINE_WIDTH}
          height={SPARKLINE_HEIGHT}
          formatValue={(v) => formatReportValue(v, r.format)}
          label={r.label}
        />
      )),
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      minWidth: PERIOD_MIN_WIDTH,
      render: (r) => (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {r.missing ? '—' : formatReportValue(r.total, r.format)}
        </Box>
      ),
    },
    ...newestFirst.map((p) => ({
      key: p.key,
      header: p.label,
      numeric: true,
      minWidth: PERIOD_MIN_WIDTH,
      render: (r: ReportRow) => (r.missing ? '—' : formatReportValue(r.values[p.i], r.format)),
    })),
  ];

  const body = (fill: boolean) => (
    <DataTable
      ariaLabel={table.title}
      columns={columns}
      groups={table.groups.map((g) => ({ key: g.title, title: g.title, rows: g.rows }))}
      getRowKey={(r) => r.key}
      fill={fill}
    />
  );

  return (
    <Panel
      title={table.title}
      subtitle={table.subtitle}
      tools={
        <>
          {cohort.control}
        </>
      }
      actions={
        <>
          {download.button(block, { disabled: loading, grain: cohort.grain })}
        </>
      }
      footer={download.problem(block)}
      loading={loading || cohort.regrouping}
      loadingHeight={240}
      collapsible
      flush
      expanded={{ content: body(true) }}
    >
      {body(false)}
    </Panel>
  );
}
